/**
 * Signature capture: type it, draw it, or upload a scan.
 *
 * A typed signature is rendered server-side in a real embedded script font
 * rather than being rasterised here, so it stays crisp at any zoom and remains
 * selectable text. Drawn and uploaded signatures become images with their
 * transparency preserved as a soft mask.
 */

import { useEffect, useRef, useState } from 'react'

export type SignatureResult =
  | { kind: 'typed'; text: string }
  | { kind: 'image'; blob: Blob; name: string }

interface Props {
  open: boolean
  defaultName?: string
  onCancel: () => void
  onConfirm: (result: SignatureResult) => void
}

type Mode = 'type' | 'draw' | 'upload'

export function SignatureDialog({ open, defaultName = '', onCancel, onConfirm }: Props) {
  const [mode, setMode] = useState<Mode>('type')
  const [typed, setTyped] = useState(defaultName)
  const [file, setFile] = useState<File | null>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [hasInk, setHasInk] = useState(false)

  useEffect(() => {
    if (open) {
      setTyped(defaultName)
      setFile(null)
      setHasInk(false)
    }
  }, [open, defaultName])

  if (!open) return null

  const confirm = () => {
    if (mode === 'type' && typed.trim()) {
      onConfirm({ kind: 'typed', text: typed.trim() })
    } else if (mode === 'upload' && file) {
      onConfirm({ kind: 'image', blob: file, name: file.name })
    } else if (mode === 'draw' && hasInk) {
      canvasRef.current?.toBlob((blob) => {
        if (blob) onConfirm({ kind: 'image', blob, name: 'signature.png' })
      }, 'image/png')
    }
  }

  const ready =
    (mode === 'type' && typed.trim().length > 0) ||
    (mode === 'upload' && file !== null) ||
    (mode === 'draw' && hasInk)

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="Add signature">
      <div className="modal__panel">
        <h2 className="modal__title">Add signature</h2>

        <div className="modal__tabs" role="tablist">
          {(['type', 'draw', 'upload'] as Mode[]).map((m) => (
            <button
              key={m}
              role="tab"
              aria-selected={mode === m}
              className="modal__tab"
              onClick={() => setMode(m)}
            >
              {m === 'type' ? 'Type' : m === 'draw' ? 'Draw' : 'Upload'}
            </button>
          ))}
        </div>

        {mode === 'type' && (
          <div className="modal__body">
            <label className="field">
              <span>Your name</span>
              <input
                autoFocus
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder="Dave Lavoie"
              />
            </label>
            <div className="sig-preview" aria-hidden="true">
              {typed || 'Your signature'}
            </div>
            <p className="modal__hint">
              Rendered in an embedded script font, so it stays sharp at any zoom
              and remains searchable text.
            </p>
          </div>
        )}

        {mode === 'draw' && (
          <div className="modal__body">
            <DrawPad canvasRef={canvasRef} onInk={() => setHasInk(true)} />
            <button
              className="linkbtn"
              onClick={() => {
                const canvas = canvasRef.current
                const context = canvas?.getContext('2d')
                if (canvas && context) {
                  context.clearRect(0, 0, canvas.width, canvas.height)
                  setHasInk(false)
                }
              }}
            >
              Clear
            </button>
          </div>
        )}

        {mode === 'upload' && (
          <div className="modal__body">
            <label className="field">
              <span>Signature image</span>
              <input
                type="file"
                accept="image/png,image/jpeg"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </label>
            <p className="modal__hint">
              A PNG with a transparent background composites cleanly over the
              page; a JPEG will carry its white box with it.
            </p>
          </div>
        )}

        <div className="modal__footer">
          <button className="btn" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn btn--primary" disabled={!ready} onClick={confirm}>
            Place signature
          </button>
        </div>
      </div>
    </div>
  )
}

function DrawPad({
  canvasRef,
  onInk,
}: {
  canvasRef: React.RefObject<HTMLCanvasElement>
  onInk: () => void
}) {
  const drawing = useRef(false)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    // Backing store at device resolution keeps the stroke smooth.
    const dpr = window.devicePixelRatio || 1
    canvas.width = 460 * dpr
    canvas.height = 150 * dpr
    const context = canvas.getContext('2d')
    if (!context) return
    context.scale(dpr, dpr)
    context.lineWidth = 2.2
    context.lineCap = 'round'
    context.lineJoin = 'round'
    context.strokeStyle = '#101010'
  }, [canvasRef])

  const point = (event: React.PointerEvent) => {
    const canvas = canvasRef.current!
    const rect = canvas.getBoundingClientRect()
    return { x: event.clientX - rect.left, y: event.clientY - rect.top }
  }

  return (
    <canvas
      ref={canvasRef}
      className="sig-pad"
      style={{ width: 460, height: 150 }}
      onPointerDown={(e) => {
        const context = canvasRef.current?.getContext('2d')
        if (!context) return
        drawing.current = true
        const { x, y } = point(e)
        context.beginPath()
        context.moveTo(x, y)
        onInk()
      }}
      onPointerMove={(e) => {
        if (!drawing.current) return
        const context = canvasRef.current?.getContext('2d')
        if (!context) return
        const { x, y } = point(e)
        context.lineTo(x, y)
        context.stroke()
      }}
      onPointerUp={() => {
        drawing.current = false
      }}
      onPointerLeave={() => {
        drawing.current = false
      }}
    />
  )
}
